import firebase from 'firebase/compat/app';
import {
  Prequal,
  PrequalApprovalStatus,
  LeadOrigin,
  PrequalApplicationStatus,
  FullApplicationStatus,
  ShoppingSegmentType,
} from '../schema';
import { withExpVariant } from './kamExpVariant';

// PostMessage and tracking interfaces
export interface LendAPIPostMessageEvent {
  origin: string;
  data: {
    type: 'formSubmission' | 'pageLoad' | 'dimensionChange' | 'iframeHeightChange';
    payload?: {
      tab_name: string;
      [key: string]: any;
    };
    height?: number;
  };
}

export interface LendAPITrackingResponse {
  success?: boolean;
  approvalStatus?: string;
  lendAPIData?: {
    applicationId?: string;
    purchasingPower?: number | string;
  };
}

export interface LendAPIStoredUserData {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  customParams: string | null;
}

export interface LendAPIPayload {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  pre_qual_fico?: string;
  estimated_down_payment?: number;
  tab_name?: string;
  // LendAPI specific fields from actual payload
  session?: string;
  app_id?: string | number; // From complete API response
  desired_monthly_payment?: number;
  pre_qual_int?: number;
  max_term_in_months_non_prime?: number;
  msg?: string;
  status?: string;
  buying_stage?: string;
  loan_amount?: number;
  app_url_params?: {
    custom?: string;
  };
  custom?: string;
  // TRIDENT-999: a BoatTrader listing URL typed/pasted (or LendAPI-prefilled)
  // on the Loan Info tab. See processCollateralURL in LendAPIFullApp.tsx /
  // LendAPICombinedFunnel.tsx.
  boat_url?: string;
  [key: string]: any;
}

/**
 * Maps LendAPI buying_stage values to ShoppingSegmentType values.
 *
 * @param {string | undefined | null} buyingStage - The buying_stage value from LendAPI
 * @return {ShoppingSegmentType | undefined} The mapped ShoppingSegmentType value or undefined if no match
 */
export function mapBuyingStageToShoppingSegment(
  buyingStage: string | undefined | null
): ShoppingSegmentType | undefined {
  if (!buyingStage) {
    return undefined;
  }

  switch (buyingStage.toLowerCase()) {
    case 'passive':
      return ShoppingSegmentType['Within 3 - 6 months'];
    case 'making offer':
      return ShoppingSegmentType['Within 3 months'];
    case 'offer accepted':
      return ShoppingSegmentType['Right Away'];
    default:
      return undefined;
  }
}

export interface DecodedQueryParams {
  [key: string]: string;
}

export function decodeLendAPICustomField(customField?: string | null): DecodedQueryParams {
  if (!customField) {
    return {};
  }

  try {
    const decoded = decodeURIComponent(customField);
    const params = new URLSearchParams(decoded);
    const result: DecodedQueryParams = {};

    params.forEach((value, key) => {
      result[key] = value;
    });

    return result;
  } catch (error) {
    console.error('[LendAPI] Error decoding custom field:', error);
    return {};
  }
}

export function generateLendAPIStorageKey(imtId?: string | number | null): string {
  const stringImtId = imtId ? String(imtId).trim() : null;
  const key = stringImtId ? `prequal-${stringImtId}` : 'prequal-NONBDP';
  return key;
}

function getIMTIdFromParams(customParams: string): { imtId?: string } {
  try {
    const params = new URLSearchParams(customParams);
    const boatInfoParam = params.get('boatInfo');
    if (boatInfoParam) {
      const boatInfo = JSON.parse(decodeURIComponent(boatInfoParam));
      return boatInfo.imtId ? { imtId: boatInfo.imtId } : {};
    }
  } catch (e) {
    console.error('[LendAPI] Error parsing boatInfo from customParams:', e);
  }
  return {};
}

export function mapLendAPIToPrequalSchema(
  payload: LendAPIPayload
): Partial<Prequal> & { customParams?: string } {
  // Extract and decode custom parameters from payload
  const encodedCustomParams = payload.app_url_params?.custom || payload.custom || undefined;
  const customParams = encodedCustomParams ? decodeURIComponent(encodedCustomParams) : undefined;

  // Extract variant from decoded custom parameters if present
  let variant: string | undefined;
  if (customParams) {
    const params = new URLSearchParams(customParams);
    variant = params.get('variant') || undefined;
  }

  const prequal: Partial<Prequal> & { customParams?: string } = {
    firstName: payload.first_name,
    lastName: payload.last_name,
    email: payload.email,
    phone: payload.phone,
    creditScore: payload.pre_qual_fico as any, // LendAPI sends string, our schema expects enum
    downPayment: payload.estimated_down_payment,
    lendAPIPrequal: true,
    createdAt: Date.now(),

    // LendAPI-specific fields
    loanAmount: payload.loan_amount,
    prequalApplicationId: payload.session || undefined,
    prequalApprovalMonthlyPayment: payload.desired_monthly_payment,

    // Default values for LendAPI flow
    leadOrigin: LeadOrigin.lendAPIPrequal,
    prequalApplicationStatus: PrequalApplicationStatus.submitted,
    fullApplicationStatus: FullApplicationStatus.notStarted,
    shoppingSegment:
      mapBuyingStageToShoppingSegment(payload.buying_stage) ||
      ShoppingSegmentType['Within 3 - 6 months'],
    completedAt: Date.now(),

    // Store raw custom parameters for later retrieval
    ...(customParams && { customParams }),

    // Extract imtId from decoded customParams.boatInfo if it exists
    ...(customParams && getIMTIdFromParams(customParams)),

    // Store variant if present in custom parameters
    ...(variant && { variant }),
  };

  if (payload.address || payload.city || payload.state || payload.zip_code) {
    prequal.address = {
      address1: payload.address || '',
      city: payload.city || '',
      state: payload.state || '',
      zip: payload.zip_code || '',
      country: 'US',
    };
  }

  return prequal;
}

export function storeLendAPIPrequalData(data: any, imtId?: string | null): void {
  try {
    const key = generateLendAPIStorageKey(imtId);

    const existingData = localStorage.getItem(key);
    if (existingData) {
      localStorage.removeItem(key);
    }

    // Clean up any existing temporary imtId key
    const existingImtIdKey = localStorage.getItem('current-lendapi-prequal-imtid');
    if (existingImtIdKey) {
      localStorage.removeItem('current-lendapi-prequal-imtid');
    }

    localStorage.setItem(key, JSON.stringify(data));

    // Store the current LendAPI prequal imtId separately for later retrieval
    const imtIdToStore = imtId && String(imtId).trim() ? String(imtId).trim() : 'NONBDP';
    localStorage.setItem('current-lendapi-prequal-imtid', imtIdToStore);
  } catch (error) {
    console.error('[LendAPI] Error storing prequal data:', error);
  }
}

// LendAPI Tracking Utilities

export interface LendAPITrackingData {
  response: LendAPITrackingResponse;
  finalMappedData: any;
  completedEvent: any;
}

export interface TrackingEventData {
  currency?: string;
  value?: string;
  transaction_id?: string;
  disqualified_lead_reason?: string;
  action_type?: string;
  action_label?: string;
  funnel?: string;
  trident_variant_string?: string;
}

/**
 * Determines if an application is explicitly not approved based on response status
 */
export function isApplicationNotApproved(response: LendAPITrackingResponse): boolean {
  return Boolean(response?.approvalStatus && response.approvalStatus !== 'Approved');
}

/**
 * Creates tracking event data for declined applications
 */
export function createDeclinedTrackingData(
  response: LendAPITrackingResponse,
  finalMappedData: any
): TrackingEventData {
  return {
    currency: 'USD',
    value: (parseFloat(response.lendAPIData?.purchasingPower?.toString() || '0') || 0).toFixed(2),
    transaction_id:
      response.lendAPIData?.applicationId || finalMappedData.prequalApplicationId || 'Unknown',
    disqualified_lead_reason: 'did not meet requirements',
    funnel: 'lendapi pre-qual',
  };
}

/**
 * Creates tracking event data for approved/fallback applications
 */
export function createApprovedTrackingData(
  response: LendAPITrackingResponse,
  finalMappedData: any,
  completedEvent: any
): TrackingEventData {
  return {
    action_type: 'Boat',
    action_label: completedEvent.eventData?.loanType || 'Unknown',
    transaction_id:
      response?.lendAPIData?.applicationId || finalMappedData.prequalApplicationId || 'Unknown',
    value: (parseFloat(response?.lendAPIData?.purchasingPower?.toString() || '0') || 0).toFixed(2),
    currency: 'USD',
    funnel: 'lendapi pre-qual',
  };
}

/**
 * Sends Kameleoon goal conversion tracking
 */
export function sendKameleoonGoal(goalId: string, behaviorType?: string): void {
  if (window.kameleoonQueue) {
    window.kameleoonQueue.push(['Goals.processConversion', goalId]);
    const behaviorMessage = behaviorType ? ` | (${behaviorType} behavior)` : '';
    console.log(`[LendAPI][TRACKING] Kameleoon custom goal sent: ${goalId}${behaviorMessage}`);
  }
}

/**
 * Main tracking orchestrator - handles the complete tracking flow for LendAPI responses
 */
export function processLendAPITrackingEvents(
  trackingData: LendAPITrackingData,
  pushToDataLayer: (event: string, data: TrackingEventData) => void,
  trackingEvents: any
): void {
  const { response, finalMappedData, completedEvent } = trackingData;

  if (isApplicationNotApproved(response)) {
    // Handle declined applications
    const declinedData = createDeclinedTrackingData(response, finalMappedData);

    pushToDataLayer(trackingEvents.GA_EVENTS.DISQUALIFY_LEAD, declinedData);
    console.log('[LendAPI][TRACKING] GA disqualify_lead event sent');

    sendKameleoonGoal(trackingEvents.KAMELEOON_GOALS.UNQUALIFIED);
  } else {
    // Handle approved or fallback applications
    const approvedData = createApprovedTrackingData(response, finalMappedData, completedEvent);

    pushToDataLayer(trackingEvents.GA_EVENTS.APPLICATION_SUBMITTED, withExpVariant(approvedData));

    const isActuallyApproved = response?.approvalStatus === 'Approved';
    const behaviorType = isActuallyApproved ? 'approved' : 'fallback';
    console.log(
      `[LendAPI][TRACKING] GA application_submitted event sent | (${behaviorType} behavior)`
    );

    sendKameleoonGoal(trackingEvents.KAMELEOON_GOALS.RESULTS, behaviorType);
  }
}

// Utility functions for LendAPI iframe and data processing

export const enhancePayloadWithStoredData = (
  payload: LendAPIPayload,
  storedData: LendAPIStoredUserData
): LendAPIPayload => {
  const enhanced = { ...payload };

  if (!enhanced.custom && storedData.customParams) {
    enhanced.custom = storedData.customParams;
  }

  return enhanced;
};

export const applyStoredDataFallback = (
  mappedData: any,
  storedData: LendAPIStoredUserData
): any => {
  const enhanced = { ...mappedData };

  if (!enhanced.email && storedData.email) {
    enhanced.email = storedData.email;
  }
  if (!enhanced.firstName && storedData.firstName) {
    enhanced.firstName = storedData.firstName;
  }
  if (!enhanced.lastName && storedData.lastName) {
    enhanced.lastName = storedData.lastName;
  }
  if (!enhanced.customParams && storedData.customParams) {
    enhanced.customParams = storedData.customParams;
  }

  return enhanced;
};

// Webhook calling utilities

const WEBHOOK_URL_PROD = 'https://us-central1-trident-funding.cloudfunctions.net/lendAPIWebhook';
const WEBHOOK_URL_STAGE = 'https://us-central1-stage-trident.cloudfunctions.net/lendAPIWebhook';

export async function callLendAPIWebhook(
  applicationId: string,
  isProduction: boolean,
  productIdQA: number,
  productIdProd: number
): Promise<void> {
  const webhookUrl = isProduction ? WEBHOOK_URL_PROD : WEBHOOK_URL_STAGE;
  const productId = isProduction ? productIdProd : productIdQA;

  const payload = {
    type: 'application.iFrameTriggered',
    product_id: productId,
    data: {
      app_id: applicationId,
    },
  };

  console.log('[LendAPI] Calling webhook with payload:', payload);

  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Webhook call failed: ${response.status} ${response.statusText}`);
  }

  console.log('[LendAPI] Webhook call successful');
}

export async function callLendAPIWebhookWithProductId(
  applicationId: string,
  productId: number,
  isProduction: boolean,
  prodTesting?: boolean,
  e2eProdTesting?: boolean
): Promise<void> {
  const webhookUrl = isProduction ? WEBHOOK_URL_PROD : WEBHOOK_URL_STAGE;

  const payload: Record<string, any> = {
    type: 'application.iFrameTriggered',
    product_id: productId,
    data: {
      app_id: applicationId,
    },
  };

  if (prodTesting) {
    payload.prodTesting = true;
  }

  if (e2eProdTesting) {
    payload.e2eProdTesting = true;
  }

  console.log('[LendAPI] Calling webhook with payload:', payload);

  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Webhook call failed: ${response.status} ${response.statusText}`);
  }

  console.log('[LendAPI] Webhook call successful');
}

export interface CombinedFullAppWebhookResult {
  docUploadUrl?: string;
  autoRequestedDocs?: string[];
  error?: string;
}

export async function callLendAPIWebhookForCombinedFullApp(
  applicationId: string,
  productId: number,
  isProductionEnv: boolean,
  prodTesting?: boolean,
  e2eProdTesting?: boolean
): Promise<CombinedFullAppWebhookResult> {
  const webhookUrl = isProductionEnv ? WEBHOOK_URL_PROD : WEBHOOK_URL_STAGE;

  const payload: Record<string, any> = {
    type: 'application.iFrameTriggered.fullAppSubmitted',
    product_id: productId,
    data: {
      app_id: applicationId,
    },
  };

  if (prodTesting) {
    payload.prodTesting = true;
  }

  if (e2eProdTesting) {
    payload.e2eProdTesting = true;
  }

  console.log('[LendAPI Combined] Calling fullApp webhook with payload:', payload);

  const response = await fetch(webhookUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(
      `[LendAPI Combined] FullApp webhook call failed: ${response.status} ${response.statusText}`
    );
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('[LendAPI Combined] FullApp webhook did not return expected JSON response');
  }

  const result: CombinedFullAppWebhookResult = await response.json();

  if (result.error) {
    throw new Error(`[LendAPI Combined] FullApp webhook failed: ${result.error}`);
  }

  console.log('[LendAPI Combined] FullApp webhook call successful, result:', result);
  return result;
}

const APPROVAL_DATA_FETCH_TIMEOUT_MS = 8000;

export interface LendAPIApprovalData {
  approvalOdds?: number;
  approvalOutcome?: string;
}

/**
 * Fetches approval_odds and approval_outcome for a LendAPI application via the
 * getLendAPIApprovalData Cloud Function (httpsCallable). Returns {} on any
 * failure (timeout, callable error, network error) so callers can use the
 * result unconditionally; the GA event payload spread will simply omit missing
 * fields. The Firebase callable SDK attaches the caller's Firebase Auth ID
 * token automatically — the funnel auto-signs anonymous users via
 * useUser.ts, so the protocol-level auth check passes for all callers.
 *
 * isProductionEnv is unused — the Firebase SDK auto-targets the right project
 * based on the initialized Firebase config — but kept in the signature to
 * avoid churning call sites.
 */
export async function fetchLendAPIApprovalData(
  applicationId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isProductionEnv: boolean,
  prodTesting?: boolean,
  e2eProdTesting?: boolean
): Promise<LendAPIApprovalData> {
  const functions = firebase.functions();
  const callable = functions.httpsCallable('getLendAPIApprovalData');

  const payload: Record<string, unknown> = { applicationId };
  if (prodTesting) {
    payload.prodTesting = true;
  }
  if (e2eProdTesting) {
    payload.e2eProdTesting = true;
  }

  // Manual client-side timeout: httpsCallable doesn't expose AbortSignal, so we
  // race the call against a setTimeout-driven rejection to preserve the 8s
  // budget the parent IIFE / Submitted hook are designed around.
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () => reject(new Error('fetchLendAPIApprovalData client timeout')),
      APPROVAL_DATA_FETCH_TIMEOUT_MS
    );
  });

  try {
    const result = await Promise.race([callable(payload), timeoutPromise]);
    const parsed = (result?.data ?? {}) as Record<string, unknown>;
    const out: LendAPIApprovalData = {};
    if (typeof parsed.approvalOdds === 'number') {
      out.approvalOdds = parsed.approvalOdds;
    }
    if (typeof parsed.approvalOutcome === 'string' && parsed.approvalOutcome.length > 0) {
      out.approvalOutcome = parsed.approvalOutcome;
    }
    return out;
  } catch (error) {
    console.warn('[LendAPI] fetchLendAPIApprovalData error:', error);
    return {};
  }
}
